import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    InputAdornment,
    IconButton,
    List,
    ListItem,
    Skeleton,
    Fade,
    Stack,
    useTheme,
    Tooltip,
    
} from '@mui/material';
import {
    Search as SearchIcon,
    WhatsApp as WhatsAppIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    ContactPhone as ContactIcon
} from '@mui/icons-material';
import { cicassService } from "../../../_services";
import { config } from "_helpers";
import { useTranslation } from "react-i18next";
import { 
    Accordion, 
    AccordionSummary, 
    AccordionDetails 
} from '@mui/material';
import * as XLSX from 'xlsx';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';

const RubricaPage = props => {
    const { t } = useTranslation();
    const [contacts, setContacts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const theme = useTheme();
    const [isMobile, setIsMobile] = useState(window.innerWidth < config.mobileSizeCheck);
    const [loading, setLoading] = useState(true); // <--- Aggiungi questo
    const [expanded, setExpanded] = useState({});


    useEffect(() => {
        loadContacts();
    }, []);

    const handleExport = () => {
        // 1. Appiattiamo i contatti filtrati (quelli che l'utente vede a schermo)
        const dataToExport = Object.values(groupedContacts).flat().map(contact => ({
            "Nome e Cognome": contact.name,
            "Email": contact.email || "-",
            "Telefono / Cellulare": contact.mobile && contact.mobile !== "-" ? contact.mobile : "-"
        }));

        if (dataToExport.length === 0) return;

        // 2. Creazione file Excel
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        
        // UIX Pro: Impostiamo una larghezza minima delle colonne per non farle apparire tagliate
        const wscols = [
            { wch: 30 }, // Nome
            { wch: 35 }, // Email
            { wch: 20 }, // Telefono
        ];
        worksheet['!cols'] = wscols;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Contatti CRI");

        // 3. Download con nome file parlante
        const date = new Date().toISOString().split('T')[0];
        XLSX.writeFile(workbook, `Rubrica_CRI_${date}.xlsx`);
    };
    const handleVCard = (contact) => {
        const name = contact.name || "Contatto";
        const email = contact.email || "";
        const mobile = contact.mobile || "";

        // Costruzione del formato vCard (VERSION 3.0 è la più compatibile)
        const vcard = [
            "BEGIN:VCARD",
            "VERSION:3.0",
            `FN:${name}`,
            `N:;${name};;;`,
            `TEL;TYPE=CELL:${mobile}`,
            `EMAIL;TYPE=INTERNET:${email}`,
            "ORG:Croce Rossa Italiana - Comitato di Merate", // Aiuta la catalogazione in rubrica
            "END:VCARD"
        ].join("\n");

        // Creazione del file e download
        const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        
        link.href = url;
        link.setAttribute("download", `${name.replace(/\s+/g, '_')}_CRI.vcf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };
    const loadContacts = async () => {
        setLoading(true); // Assicurati che sia true all'inizio
        try {
            // Endpoint fornito: agency/addressbook
            const data = await cicassService.getAll("ana_agency/addressbook");
            // Ordiniamo alfabeticamente per nome subito dopo il fetch
            const sortedData = (data.data || []).sort((a, b) => a.name.localeCompare(b.name));
            setContacts(sortedData);
        } catch (error) {
            console.error("Errore nel caricamento della rubrica:", error);
        }
        finally {
            setLoading(false); // <--- Spegne il loading sia in caso di successo che di errore
        }
    };

    // Filtro e Raggruppamento logico (A, B, C...)
    const groupedContacts = useMemo(() => {
        const filtered = contacts.filter(c =>
            c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.email.toLowerCase().includes(searchTerm.toLowerCase())
        );

        return filtered.reduce((groups, contact) => {
            const letter = contact.name.charAt(0).toUpperCase();
            if (!groups[letter]) groups[letter] = [];
            groups[letter].push(contact);
            return groups;
        }, {});
    }, [contacts, searchTerm]);

    const alphabet = Object.keys(groupedContacts).sort();

    const handleWhatsApp = (mobile) => {
        const cleanNumber = mobile.replace(/\D/g, '');
        window.open(`https://wa.me/${cleanNumber}`, '_blank');
    };

    const ActionButtons = ({ contact }) => (
        <Stack direction="row" spacing={1}>
            <Tooltip title={t("Salva in Rubrica")}>
                <IconButton size="small" color="primary" onClick={() => handleVCard(contact)}>
                    <PersonAddIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            {contact.email && (
                <Tooltip title={t("Invia Email")}>
                    <IconButton size="small" color="primary" onClick={() => window.location.href = `mailto:${contact.email}`}>
                        <EmailIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            {contact.mobile && contact.mobile !== "-" && (
                <>
                    <Tooltip title={t("Chiama")}>
                        <IconButton size="small" color="success" onClick={() => window.location.href = `tel:${contact.mobile}`}>
                            <PhoneIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("WhatsApp")}>
                        <IconButton size="small" sx={{ color: '#25D366' }} onClick={() => handleWhatsApp(contact.mobile)}>
                            <WhatsAppIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </>
            )}
        </Stack>
    );
    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(prev => ({ ...prev, [panel]: isExpanded }));
    };
    return (
        <Box sx={{ m: 2 }}>
            {/* TITOLO E BARRA DI RICERCA: Sempre visibili per evitare vicoli ciechi nella UX */}
            <div style={{ margin: "1%", marginTop: "1%" }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                    {t("Rubrica")}
                </Typography>
            </div>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                <TextField
                    placeholder="Cerca contatto..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 400 }, backgroundColor: 'white' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
                
                {/* Il download viene rimosso se non ci sono contatti mostrati a schermo */}
                {alphabet.length > 0 && (
                    <Tooltip title="Scarica Rubrica (Excel)">
                        <IconButton 
                            onClick={handleExport}
                            sx={{ 
                                backgroundColor: theme.palette.primary.main, 
                                color: 'white',
                                borderRadius: '8px',
                                px: isMobile ? 1 : 2,
                                '&:hover': { backgroundColor: theme.palette.primary.dark }
                            }}
                        >
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            {/* CONTENUTO DINAMICO: Cambia in base allo stato dei dati */}
            {loading === true ? (
                // Stato 1: Caricamento in corso (Mostra Skeleton)
                <Box>
                    <Skeleton variant="text" width={40} height={32} sx={{ mb: 1, ml: 2 }} />
                    {[1, 2].map((i) => (
                        <Paper key={i} elevation={0} sx={{ mb: 1, border: '1px solid #eee', p: 1.5 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Skeleton variant="circular" width={40} height={40} />
                                <Box sx={{ flexGrow: 1 }}>
                                    <Skeleton variant="text" width="30%" />
                                    <Skeleton variant="text" width="50%" />
                                </Box>
                            </Stack>
                        </Paper>
                    ))}
                </Box>
            ) : alphabet.length > 0 ? (
                // Stato 2: Dati presenti (Layout con Accordion e Righe Slim ottimizzate per mobile)
                <Fade in={true} timeout={500}>
                    <Box>
                        {alphabet.map((letter) => (
                            <Accordion 
                                key={letter} 
                                expanded={searchTerm.length > 0 ? true : !!expanded[letter]}
                                onChange={handleAccordionChange(letter)}
                                sx={{ 
                                    mb: 1, 
                                    border: '1px solid #eee',
                                    '&:before': { display: 'none' }, 
                                    boxShadow: 'none'
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                        <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: theme.palette.primary.main }}>
                                            {letter}
                                        </Typography>
                                        <Box sx={{ 
                                            backgroundColor: theme.palette.primary.main, 
                                            color: 'white',
                                            px: 1.5, py: 0.2, 
                                            borderRadius: '12px', 
                                            fontSize: '0.75rem', 
                                            fontWeight: 'bold',
                                            border: `1px solid ${theme.palette.primary.main}`
                                        }}>
                                            {groupedContacts[letter].length} {t("contatti")}
                                        </Box>
                                    </Stack>
                                </AccordionSummary>
                                
                                <AccordionDetails sx={{ p: 0 }}>
                                    <List disablePadding>
                                        {groupedContacts[letter].map((contact) => (
                                            <ListItem
                                                key={contact.id}
                                                divider
                                                sx={{ 
                                                    py: 1, 
                                                    px: 2,
                                                    display: 'flex',
                                                    flexDirection: 'row', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'space-between', 
                                                    '&:hover': { backgroundColor: '#fafafa' }
                                                }}
                                            >
                                                {/* Dettagli Contatto Allineati a Sinistra */}
                                                <Box sx={{ flexGrow: 1, minWidth: 0 }}> 
                                                    <Typography 
                                                        variant="subtitle1" 
                                                        sx={{ 
                                                            fontWeight: 700, 
                                                            color: '#1a3e72', 
                                                            lineHeight: 1.2,
                                                            whiteSpace: 'nowrap', 
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis' 
                                                        }}
                                                    >
                                                        {contact.name}
                                                    </Typography>
                                                    <Typography 
                                                        variant="caption" 
                                                        color="text.secondary" 
                                                        sx={{ display: 'block', mt: 0.2 }}
                                                    >
                                                        {contact.email}
                                                    </Typography>
                                                    {contact.mobile && contact.mobile !== "-" && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {contact.mobile}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {/* Icone Azione Compatte sulla Destra */}
                                                <Box sx={{ ml: 1, flexShrink: 0 }}>
                                                    <ActionButtons contact={contact} />
                                                </Box>
                                            </ListItem>
                                        ))}
                                    </List>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                </Fade>
            ) : (
                // Stato 3: Nessun contatto trovato (La barra rimane sopra, l'utente può correggere l'input)
                <Fade in={true}>
                    <Stack alignItems="center" sx={{ mt: 10, opacity: 0.5 }}>
                        <ContactIcon sx={{ fontSize: 60, mb: 2 }} />
                        <Typography variant="h6">{t("Nessun contatto trovato")}</Typography>
                    </Stack>
                </Fade>
            )}
        </Box>
    );
};

export default RubricaPage;